import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import { kokoroClient } from '../zerovoice/kokoroClient';
import {
  ensureXxhash,
  formatHexSeed,
  parseHexSeed,
} from '../zerovoice/xxhash';
import {
  KOKORO_VOICES,
  resolveKokoroVoice,
  isValidKokoroVoiceId,
} from '../zerovoice/voiceMapping';
import type { KokoroVoiceId } from '../zerovoice/voiceMapping';
import {
  deletePin,
  listPins,
  upsertPin,
} from '../zerovoice/pinsClient';
import type {
  ArcProperties,
  KokoroAgentGender,
  KokoroBlendMode,
  KokoroGender,
  KokoroModelState,
  KokoroPin,
  TTSProvider,
  VoiceBundleState,
  VoiceSpec,
} from '../zerovoice/types';
import type { SpeechFallback } from '../hooks/speechResolver';
import { playAudio } from '../hooks/useAudioPlayer';
import { ZeroVoiceAgentRow } from './ZeroVoiceAgentRow';
import { ZeroVoiceArcInspector } from './ZeroVoiceArcInspector';
import { CustomizeSpecDialog } from './CustomizeSpecDialog';
import { voiceBundle } from '../zerovoice/voiceBundle';
import { buildArcTable } from '../zerovoice/arcProperties';
import {
  resolveKokoroSpec,
  formatSpec,
} from '../zerovoice/kokoroSpec';
import {
  invalidateArcTable,
  synthesizeBlendLocal,
} from '../zerovoice/speakKokoroBlend';
import { ZeroTTSSettingsPanel } from '../zerovoice/zeroTTSSettingsPanel';
import {
  asZeroTTSDepth,
  type ZeroTTSDepth,
} from '../zerovoice/zeroTTSProfile';

interface PersonaListItem {
  id: string;
  name: string;
}
interface PersonaListResponse {
  personas: PersonaListItem[];
}
interface SettingsResponse {
  settings: Record<string, string>;
}

const DEFAULT_SEED = 'DEADBEEFC0FFEE';
const API_BASE = import.meta.env.VITE_API_BASE as string;

function isGender(v: string): v is KokoroGender {
  return v === 'female' || v === 'male' || v === 'mixed';
}

export function ZeroVoiceTab({ onChanged }: { onChanged: () => void }) {
  const [provider, setProvider] = useState<TTSProvider>('gemini');
  const [blendMode, setBlendMode] = useState<KokoroBlendMode>('single');
  const [worldSeed, setWorldSeed] = useState<string>(DEFAULT_SEED);
  const [seedDraft, setSeedDraft] = useState<string>(DEFAULT_SEED);
  const [defaultGender, setDefaultGender] = useState<KokoroGender>('mixed');
  const [fallback, setFallback] = useState<boolean>(true);
  // Stage 12 (D4): CONVO retention — 0 means never auto-delete.
  const [convoRetentionDays, setConvoRetentionDays] = useState<number>(0);
  // Stage 12 (E3): customize-spec dialog state.
  const [customizeAgent, setCustomizeAgent] = useState<string | null>(null);
  const [customizeInitial, setCustomizeInitial] = useState<VoiceSpec | null>(
    null,
  );

  const [modelState, setModelState] =
    useState<KokoroModelState>(kokoroClient.getState());
  const [progress, setProgress] = useState<number>(kokoroClient.getProgress());

  const [bundleState, setBundleState] =
    useState<VoiceBundleState>(voiceBundle.getState());
  const [bundleProgress, setBundleProgress] =
    useState<number>(voiceBundle.getProgress());

  const [arcTable, setArcTable] = useState<ArcProperties[][] | null>(null);
  const [arcInspectorOpen, setArcInspectorOpen] = useState(false);

  // Stage 9: speech-summary settings.
  const [summaryEnabled, setSummaryEnabled] = useState<boolean>(true);
  const [summaryFallback, setSummaryFallback] =
    useState<SpeechFallback>('first-sentence');

  // Stage 26: ZeroTTS (cached-speech filler) settings.
  const [zeroTTSEnabled, setZeroTTSEnabled] = useState<boolean>(true);
  const [zeroTTSFireAck, setZeroTTSFireAck] = useState<boolean>(true);
  const [zeroTTSFireToolInvoke, setZeroTTSFireToolInvoke] =
    useState<boolean>(true);
  const [zeroTTSFireProgress, setZeroTTSFireProgress] =
    useState<boolean>(false);
  const [zeroTTSFireError, setZeroTTSFireError] = useState<boolean>(true);
  // Stage 40: cache depth (classic / medium / long). Picks which profile
  // JSON the loader fetches AND folds into the voice fingerprint.
  const [zeroTTSDepth, setZeroTTSDepth] = useState<ZeroTTSDepth>('classic');
  const [activePersonaName, setActivePersonaName] = useState<string>('');

  const [xxhashReady, setXxhashReady] = useState<boolean>(false);
  const [personas, setPersonas] = useState<PersonaListItem[]>([]);
  const [pins, setPins] = useState<Record<string, KokoroPin>>({});
  const [testText, setTestText] = useState<string>('Hello, I am {agentName}.');
  const [testAgent, setTestAgent] = useState<string>('');
  const [playingKey, setPlayingKey] = useState<string | null>(null);

  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Initial load.
  useEffect(() => {
    void (async () => {
      try {
        const [settingsRes, listRes, pinMap] = await Promise.all([
          apiGet<SettingsResponse>('/api/settings'),
          apiGet<PersonaListResponse>('/api/personas'),
          listPins().catch(() => ({}) as Record<string, KokoroPin>),
        ]);
        const s = settingsRes.settings || {};
        const prov = s.tts_provider === 'kokoro' ? 'kokoro' : 'gemini';
        const mode: KokoroBlendMode =
          s.kokoro_blend_mode === 'blend' ? 'blend' : 'single';
        const seed = (s.kokoro_world_seed || DEFAULT_SEED).toUpperCase();
        const gen = isGender(s.kokoro_gender_default || '')
          ? (s.kokoro_gender_default as KokoroGender)
          : 'mixed';
        const fb = s.kokoro_fallback_to_gemini !== 'false';
        const summaryOn = s.speech_summary_enabled !== 'false';
        const fallbackRaw = s.speech_summary_fallback;
        const summaryFb: SpeechFallback =
          fallbackRaw === 'full' || fallbackRaw === 'skip'
            ? fallbackRaw
            : 'first-sentence';
        setProvider(prov);
        setBlendMode(mode);
        setWorldSeed(seed);
        setSeedDraft(seed);
        setDefaultGender(gen);
        setFallback(fb);
        setSummaryEnabled(summaryOn);
        setSummaryFallback(summaryFb);
        // Stage 26: ZeroTTS toggles. Defaults: enabled, fire on ack & tool-invoke & error.
        setZeroTTSEnabled(s.zerotts_enabled !== 'false');
        setZeroTTSFireAck(s.zerotts_fire_ack !== 'false');
        setZeroTTSFireToolInvoke(s.zerotts_fire_tool_invoke !== 'false');
        setZeroTTSFireProgress(s.zerotts_fire_progress === 'true');
        setZeroTTSFireError(s.zerotts_fire_error !== 'false');
        // Stage 40: cache depth (default 'classic' if unset/invalid).
        setZeroTTSDepth(asZeroTTSDepth(s.zerotts_depth));
        const retentionRaw = Number(s.speech_retention_convo_days ?? '0');
        setConvoRetentionDays(
          Number.isFinite(retentionRaw) && retentionRaw >= 0
            ? Math.floor(retentionRaw)
            : 0,
        );
        setPersonas(listRes.personas ?? []);
        setPins(pinMap);
        if ((listRes.personas ?? []).length > 0) {
          setTestAgent(listRes.personas[0].name);
        }
        // Stage 26: resolve active persona name for the ZeroTTS panel.
        try {
          const initRes = await apiGet<{
            activePersona: { name: string } | null;
          }>('/api/init');
          setActivePersonaName(initRes.activePersona?.name ?? '');
        } catch {
          /* leave empty — panel shows "no active persona" */
        }
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  // xxHash wasm.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await ensureXxhash();
        if (!cancelled) setXxhashReady(true);
      } catch (e) {
        if (!cancelled) {
          setErr(
            (prev) =>
              prev ??
              `xxhash-wasm init failed: ${
                e instanceof Error ? e.message : String(e)
              }`,
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Kokoro model state/progress listeners.
  useEffect(() => {
    const offS = kokoroClient.onState(setModelState);
    const offP = kokoroClient.onProgress(setProgress);
    return () => {
      offS();
      offP();
    };
  }, []);

  // Voice bundle state/progress listeners.
  useEffect(() => {
    const offS = voiceBundle.onState(setBundleState);
    const offP = voiceBundle.onProgress((loaded) => setBundleProgress(loaded));
    return () => {
      offS();
      offP();
    };
  }, []);

  // Auto-kick the voice bundle load whenever blend mode is active. Runs on
  // initial mount (if blend was on from a previous session) and whenever
  // the user flips the toggle. Idempotent — ensureReady() short-circuits
  // when already ready.
  useEffect(() => {
    if (blendMode !== 'blend') return;
    if (voiceBundle.getState() === 'ready') return;
    void voiceBundle.ensureReady().catch((e) => {
      setErr(e instanceof Error ? e.message : String(e));
    });
  }, [blendMode]);

  // Whenever bundle becomes ready or worldSeed changes in blend mode,
  // rebuild the arc table.
  useEffect(() => {
    if (blendMode !== 'blend') {
      setArcTable(null);
      return;
    }
    if (bundleState !== 'ready') return;
    if (!xxhashReady) return;
    try {
      const table = buildArcTable(parseHexSeed(worldSeed || '0'));
      setArcTable(table);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [blendMode, bundleState, worldSeed, xxhashReady]);

  const persistSettings = async (
    patch: Record<string, string>,
  ): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await apiJson('PUT', '/api/settings', patch);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onProviderChange = async (next: TTSProvider): Promise<void> => {
    setProvider(next);
    await persistSettings({ tts_provider: next });
  };

  const onBlendModeChange = async (next: KokoroBlendMode): Promise<void> => {
    setBlendMode(next);
    invalidateArcTable();
    await persistSettings({ kokoro_blend_mode: next });
    if (next === 'blend') {
      // Lazy-kick the bundle load so the inspector populates.
      void voiceBundle.ensureReady().catch((e) => {
        setErr(e instanceof Error ? e.message : String(e));
      });
    }
  };

  const commitSeed = async (): Promise<void> => {
    let next: string;
    try {
      next = formatHexSeed(parseHexSeed(seedDraft || '0'));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      return;
    }
    setWorldSeed(next);
    setSeedDraft(next);
    invalidateArcTable();
    await persistSettings({ kokoro_world_seed: next });
  };

  const regenerateSeed = async (): Promise<void> => {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    let v = 0n;
    for (const b of bytes) v = (v << 8n) | BigInt(b);
    const next = formatHexSeed(v);
    setWorldSeed(next);
    setSeedDraft(next);
    invalidateArcTable();
    await persistSettings({ kokoro_world_seed: next });
  };

  const onDefaultGenderChange = async (g: KokoroGender): Promise<void> => {
    setDefaultGender(g);
    await persistSettings({ kokoro_gender_default: g });
  };

  const onFallbackChange = async (v: boolean): Promise<void> => {
    setFallback(v);
    await persistSettings({ kokoro_fallback_to_gemini: v ? 'true' : 'false' });
  };

  const onSummaryEnabledChange = async (v: boolean): Promise<void> => {
    setSummaryEnabled(v);
    await persistSettings({ speech_summary_enabled: v ? 'true' : 'false' });
  };

  const onSummaryFallbackChange = async (v: SpeechFallback): Promise<void> => {
    setSummaryFallback(v);
    await persistSettings({ speech_summary_fallback: v });
  };

  // Stage 26: ZeroTTS toggle handlers.
  const onZeroTTSEnabledChange = async (v: boolean): Promise<void> => {
    setZeroTTSEnabled(v);
    await persistSettings({ zerotts_enabled: v ? 'true' : 'false' });
  };
  const onZeroTTSFireAckChange = async (v: boolean): Promise<void> => {
    setZeroTTSFireAck(v);
    await persistSettings({ zerotts_fire_ack: v ? 'true' : 'false' });
  };
  const onZeroTTSFireToolInvokeChange = async (
    v: boolean,
  ): Promise<void> => {
    setZeroTTSFireToolInvoke(v);
    await persistSettings({ zerotts_fire_tool_invoke: v ? 'true' : 'false' });
  };
  const onZeroTTSFireProgressChange = async (v: boolean): Promise<void> => {
    setZeroTTSFireProgress(v);
    await persistSettings({ zerotts_fire_progress: v ? 'true' : 'false' });
  };
  const onZeroTTSFireErrorChange = async (v: boolean): Promise<void> => {
    setZeroTTSFireError(v);
    await persistSettings({ zerotts_fire_error: v ? 'true' : 'false' });
  };
  // Stage 40: cache-depth persistor. Setting flips state; the panel's
  // useZeroTTS picks up the new depth, recomputes the fingerprint,
  // sees the manifest mismatch, and reports state 'idle' so the user
  // can click Install to regenerate.
  const onZeroTTSDepthChange = async (next: ZeroTTSDepth): Promise<void> => {
    setZeroTTSDepth(next);
    await persistSettings({ zerotts_depth: next });
  };

  const onConvoRetentionChange = async (days: number): Promise<void> => {
    const clamped = Math.max(0, Math.floor(days));
    setConvoRetentionDays(clamped);
    await persistSettings({ speech_retention_convo_days: String(clamped) });
  };

  const downloadModel = async (): Promise<void> => {
    setErr(null);
    try {
      await kokoroClient.ensureReady();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const clearModelCache = async (): Promise<void> => {
    setErr(null);
    try {
      await kokoroClient.clearCache();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const downloadVoices = async (): Promise<void> => {
    setErr(null);
    try {
      await voiceBundle.ensureReady();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const clearVoicesCache = async (): Promise<void> => {
    setErr(null);
    try {
      await voiceBundle.clearCache();
      invalidateArcTable();
      setArcTable(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const playLocalSingle = async (
    voiceId: KokoroVoiceId,
    text: string,
  ): Promise<void> => {
    setPlayingKey(voiceId);
    setErr(null);
    let url: string | null = null;
    try {
      const { wav } = await kokoroClient.synthesize({ text, voiceId });
      url = URL.createObjectURL(wav);
      await playAudio(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (url) URL.revokeObjectURL(url);
      setPlayingKey(null);
    }
  };

  const playLocalBlend = async (
    spec: VoiceSpec,
    text: string,
    keyOverride?: string,
  ): Promise<void> => {
    const key = keyOverride ?? `blend:${spec.voiceA}:${spec.voiceB}:${spec.t.toFixed(3)}`;
    setPlayingKey(key);
    setErr(null);
    let url: string | null = null;
    try {
      const { wav } = await synthesizeBlendLocal({
        text,
        spec,
        worldSeedHex: worldSeed,
      });
      url = URL.createObjectURL(wav);
      await playAudio(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (url) URL.revokeObjectURL(url);
      setPlayingKey(null);
    }
  };

  const runTestBench = async (): Promise<void> => {
    if (!testAgent || !xxhashReady) return;
    const text = testText.replace(/\{agentName\}/g, testAgent);
    const pin = pins[testAgent];
    if (blendMode === 'blend') {
      let spec: VoiceSpec | null = null;
      try {
        const raw = pin?.spec_json;
        if (raw) spec = JSON.parse(raw) as VoiceSpec;
      } catch {
        /* fall through to hash */
      }
      if (!spec) {
        spec = resolveKokoroSpec({
          name: testAgent,
          gender: pin?.gender ?? defaultGender,
          worldSeed: parseHexSeed(worldSeed || '0'),
        });
      }
      await playLocalBlend(spec, text, `testbench:${testAgent}`);
    } else {
      let voiceId: KokoroVoiceId;
      if (pin && isValidKokoroVoiceId(pin.voice_id)) {
        voiceId = pin.voice_id;
      } else {
        voiceId = resolveKokoroVoice({
          name: testAgent,
          gender: pin?.gender ?? defaultGender,
          worldSeed: parseHexSeed(worldSeed || '0'),
        });
      }
      await playLocalSingle(voiceId, text);
    }
  };

  const onPinToggleSingle = async (
    agentName: string,
    voiceId: KokoroVoiceId,
    gender: KokoroGender,
  ): Promise<void> => {
    setErr(null);
    try {
      const existing = pins[agentName];
      if (existing) {
        await deletePin(agentName);
        setPins((prev) => {
          const next = { ...prev };
          delete next[agentName];
          return next;
        });
      } else {
        await upsertPin(agentName, {
          voice_id: voiceId,
          gender,
          world_seed: worldSeed,
          spec_json: null,
        });
        setPins((prev) => ({
          ...prev,
          [agentName]: {
            voice_id: voiceId,
            gender,
            world_seed: worldSeed,
            spec_json: null,
            pinned_at: Math.floor(Date.now() / 1000),
          },
        }));
      }
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const onPinToggleBlend = async (
    agentName: string,
    spec: VoiceSpec,
    gender: KokoroGender,
  ): Promise<void> => {
    setErr(null);
    try {
      const existing = pins[agentName];
      if (existing) {
        await deletePin(agentName);
        setPins((prev) => {
          const next = { ...prev };
          delete next[agentName];
          return next;
        });
      } else {
        const specJson = JSON.stringify(spec);
        await upsertPin(agentName, {
          voice_id: KOKORO_VOICES[spec.voiceA],
          gender,
          world_seed: worldSeed,
          spec_json: specJson,
        });
        setPins((prev) => ({
          ...prev,
          [agentName]: {
            voice_id: KOKORO_VOICES[spec.voiceA],
            gender,
            world_seed: worldSeed,
            spec_json: specJson,
            pinned_at: Math.floor(Date.now() / 1000),
          },
        }));
      }
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const onAgentGenderChange = async (
    agentName: string,
    next: KokoroAgentGender,
  ): Promise<void> => {
    setErr(null);
    try {
      const existing = pins[agentName];
      if (next === 'default' && !existing) return;
      if (next === 'default' && existing) {
        await upsertPin(agentName, {
          voice_id: existing.voice_id,
          gender: null,
          world_seed: worldSeed,
          spec_json: existing.spec_json ?? null,
        });
        setPins((prev) => ({
          ...prev,
          [agentName]: {
            ...existing,
            gender: null,
            world_seed: worldSeed,
            pinned_at: Math.floor(Date.now() / 1000),
          },
        }));
        onChanged();
        return;
      }
      if (next === 'default') return;
      if (!xxhashReady) return;
      const concreteGender: KokoroGender = next;

      if (blendMode === 'blend') {
        // Re-derive a full spec at the new gender pool.
        const spec =
          existing && existing.spec_json
            ? (JSON.parse(existing.spec_json) as VoiceSpec)
            : resolveKokoroSpec({
                name: agentName,
                gender: concreteGender,
                worldSeed: parseHexSeed(worldSeed || '0'),
              });
        const specJson = JSON.stringify(spec);
        await upsertPin(agentName, {
          voice_id: KOKORO_VOICES[spec.voiceA],
          gender: concreteGender,
          world_seed: worldSeed,
          spec_json: specJson,
        });
        setPins((prev) => ({
          ...prev,
          [agentName]: {
            voice_id: KOKORO_VOICES[spec.voiceA],
            gender: concreteGender,
            world_seed: worldSeed,
            spec_json: specJson,
            pinned_at: Math.floor(Date.now() / 1000),
          },
        }));
      } else {
        const voiceId =
          existing && isValidKokoroVoiceId(existing.voice_id)
            ? existing.voice_id
            : resolveKokoroVoice({
                name: agentName,
                gender: concreteGender,
                worldSeed: parseHexSeed(worldSeed || '0'),
              });
        await upsertPin(agentName, {
          voice_id: voiceId,
          gender: concreteGender,
          world_seed: worldSeed,
          spec_json: null,
        });
        setPins((prev) => ({
          ...prev,
          [agentName]: {
            voice_id: voiceId,
            gender: concreteGender,
            world_seed: worldSeed,
            spec_json: null,
            pinned_at: Math.floor(Date.now() / 1000),
          },
        }));
      }
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const previewRows = useMemo(() => personas.slice(0, 20), [personas]);

  if (!loaded) return <div className="muted">Loading…</div>;

  const modelStatusText =
    modelState === 'idle'
      ? 'Not loaded'
      : modelState === 'loading'
        ? `Downloading… ${progress}%`
        : modelState === 'ready'
          ? 'Ready'
          : 'Error';

  const bundleStatusText =
    bundleState === 'idle'
      ? 'Not loaded'
      : bundleState === 'loading'
        ? `Downloading… ${bundleProgress}/${KOKORO_VOICES.length}`
        : bundleState === 'ready'
          ? `Ready (${KOKORO_VOICES.length} voices)`
          : 'Error';

  return (
    <div className="stack">
      <div className="panel">
        <h3>TTS provider</h3>
        <fieldset className="voice-toggle-row" style={{ border: 0, padding: 0 }}>
          <legend className="muted small">
            Select which engine synthesises assistant replies.
          </legend>
          <label className="voice-toggle-row">
            <input
              type="radio"
              name="tts_provider"
              value="gemini"
              checked={provider === 'gemini'}
              onChange={() => void onProviderChange('gemini')}
              disabled={busy}
            />
            <span>
              <strong>Gemini (default)</strong> — cloud-hosted Gemini 3.1 TTS.
              Rate-limited; supports director notes and multi-speaker presets.
            </span>
          </label>
          <label className="voice-toggle-row">
            <input
              type="radio"
              name="tts_provider"
              value="kokoro"
              checked={provider === 'kokoro'}
              onChange={() => void onProviderChange('kokoro')}
              disabled={busy}
            />
            <span>
              <strong>ZeroVoice (Kokoro) — local</strong> — runs entirely in
              your browser. No rate limit. Single-speaker.
            </span>
          </label>
        </fieldset>

        <div
          className="voice-toggle-row"
          role="status"
          aria-live="polite"
          style={{ marginTop: '0.5em' }}
        >
          <span>Model: {modelStatusText}</span>
          {modelState === 'loading' ? (
            <span className="muted small"> ({progress}% of ~88 MB)</span>
          ) : null}
        </div>
        <div className="voice-dropdown-row" style={{ gap: '0.5em' }}>
          <button
            type="button"
            onClick={() => void downloadModel()}
            disabled={modelState === 'loading' || modelState === 'ready'}
          >
            {modelState === 'ready' ? 'Loaded' : 'Download now'}
          </button>
          <button
            type="button"
            onClick={() => void clearModelCache()}
            disabled={modelState === 'loading'}
          >
            Clear cache
          </button>
        </div>
        <label
          className="voice-toggle-row"
          style={{ marginTop: '0.5em' }}
        >
          <input
            type="checkbox"
            checked={fallback}
            onChange={(e) => void onFallbackChange(e.target.checked)}
            disabled={busy}
          />
          <span>
            If Kokoro fails, fall back to Gemini for that utterance
          </span>
        </label>
      </div>

      <div className="panel">
        <h3>Blend mode</h3>
        <p className="muted small">
          Stage 8: blend two base voices deterministically with arc-property
          warping. Unlocks billions of unique voices from the same 26 base
          Kokoro voices.
        </p>
        <fieldset className="voice-toggle-row" style={{ border: 0, padding: 0 }}>
          <legend className="muted small">Select voice resolution strategy.</legend>
          <label className="voice-toggle-row">
            <input
              type="radio"
              name="kokoro_blend_mode"
              value="single"
              checked={blendMode === 'single'}
              onChange={() => void onBlendModeChange('single')}
              disabled={busy}
            />
            <span>
              <strong>Single voice</strong> (Stage 7) — one of 26 Kokoro voices
              per agent.
            </span>
          </label>
          <label className="voice-toggle-row">
            <input
              type="radio"
              name="kokoro_blend_mode"
              value="blend"
              checked={blendMode === 'blend'}
              onChange={() => void onBlendModeChange('blend')}
              disabled={busy}
            />
            <span>
              <strong>Blend engine</strong> (Stage 8) — two voices × slerp ×
              pitch × energy × arc warping. Deterministic, billions possible.
            </span>
          </label>
        </fieldset>

        <div
          className="voice-toggle-row"
          role="status"
          aria-live="polite"
          style={{ marginTop: '0.5em' }}
        >
          <span>Voice bundle: {bundleStatusText}</span>
        </div>
        <div className="voice-dropdown-row" style={{ gap: '0.5em' }}>
          <button
            type="button"
            onClick={() => void downloadVoices()}
            disabled={bundleState === 'loading' || bundleState === 'ready'}
          >
            {bundleState === 'ready' ? 'Loaded' : 'Download voices now'}
          </button>
          <button
            type="button"
            onClick={() => void clearVoicesCache()}
            disabled={bundleState === 'loading'}
          >
            Clear voices cache
          </button>
        </div>
      </div>

      <div className="panel">
        <h3>Speech summary</h3>
        <p className="muted small">
          Stage 9: the assistant ends each reply with a short
          <code> &lt;speech&gt; </code> summary. When enabled, only that
          summary is spoken — the full reply still appears in the transcript.
          Turn-taking stays snappy.
        </p>
        <label className="voice-toggle-row">
          <input
            type="checkbox"
            checked={summaryEnabled}
            onChange={(e) => void onSummaryEnabledChange(e.target.checked)}
            disabled={busy}
          />
          <span>Speak only the summary when available</span>
        </label>
        <div
          className="voice-dropdown-row"
          style={{ marginTop: '0.5em', gap: '0.5em' }}
        >
          <label className="muted small">If the model omits the tag:</label>
          <select
            value={summaryFallback}
            onChange={(e) =>
              void onSummaryFallbackChange(e.target.value as SpeechFallback)
            }
            disabled={busy}
          >
            <option value="first-sentence">speak the first sentence</option>
            <option value="full">speak the full reply</option>
            <option value="skip">stay silent</option>
          </select>
        </div>
      </div>

      <div className="panel">
        <h3>CONVO retention</h3>
        <p className="muted small">
          Stage 12: automatically delete CONVO (per-turn chat TTS) clips
          older than the chosen cutoff. SCRIPT clips are never auto-deleted.
          The sweep runs at most once an hour server-side.
        </p>
        <div className="voice-dropdown-row" style={{ gap: '0.5em' }}>
          <label className="muted small">Delete CONVO older than</label>
          <select
            value={String(convoRetentionDays)}
            onChange={(e) =>
              void onConvoRetentionChange(Number(e.target.value))
            }
            disabled={busy}
          >
            <option value="0">never</option>
            <option value="1">1 day</option>
            <option value="7">7 days</option>
            <option value="14">14 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
          </select>
        </div>
      </div>

      <div className="panel">
        <h3>World seed</h3>
        <p className="muted small">
          16-character hex. Changing it re-rolls every <em>unpinned</em> agent's
          voice.
        </p>
        <div className="voice-dropdown-row">
          <input
            type="text"
            value={seedDraft}
            onChange={(e) => setSeedDraft(e.target.value.toUpperCase())}
            maxLength={18}
            style={{ fontFamily: 'monospace', minWidth: 18 + 'ch' }}
            disabled={busy}
          />
          <button
            type="button"
            onClick={() => void commitSeed()}
            disabled={busy || seedDraft === worldSeed}
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => void regenerateSeed()}
            disabled={busy}
            title="Generate a random 64-bit seed"
          >
            Regenerate
          </button>
        </div>
      </div>

      <div className="panel">
        <h3>Default gender pool</h3>
        <p className="muted small">
          Used for agents without a per-row gender override.
        </p>
        <div className="voice-dropdown-row">
          <select
            value={defaultGender}
            onChange={(e) =>
              void onDefaultGenderChange(e.target.value as KokoroGender)
            }
            disabled={busy}
          >
            <option value="female">female (14 voices)</option>
            <option value="male">male (12 voices)</option>
            <option value="mixed">mixed (26 voices)</option>
          </select>
        </div>
      </div>

      <div className="panel">
        <h3>Test bench</h3>
        <p className="muted small">
          Synthesize a sample with an agent's resolved voice. Plays locally;
          does not save to R2.
        </p>
        <div className="voice-dropdown-row" style={{ gap: '0.5em' }}>
          <select
            value={testAgent}
            onChange={(e) => setTestAgent(e.target.value)}
          >
            {personas.length === 0 ? (
              <option value="">(no agents)</option>
            ) : null}
            {personas.map((p) => (
              <option key={p.id} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <textarea
          value={testText}
          onChange={(e) => setTestText(e.target.value)}
          rows={2}
          style={{ width: '100%', marginTop: '0.5em' }}
        />
        <div className="voice-dropdown-row" style={{ marginTop: '0.5em' }}>
          <button
            type="button"
            onClick={() => void runTestBench()}
            disabled={!testAgent || playingKey !== null || !xxhashReady}
          >
            {playingKey ? 'Playing…' : 'Test'}
          </button>
          <span className="muted small">
            {xxhashReady ? null : 'Initialising hash…'}
          </span>
        </div>
      </div>

      <div className="panel">
        <h3>Agents</h3>
        <p className="muted small">
          {blendMode === 'blend'
            ? 'Each agent\'s voice is derived from name + gender + world seed as a full VoiceSpec (voiceA × voiceB × t + pitch + energy). Pin to lock the spec across seed changes.'
            : 'Each agent\'s voice is derived from its name, gender pool, and the world seed. Pin to lock the current voice across seed changes.'}
        </p>
        {previewRows.length === 0 ? (
          <div className="muted">No agents yet.</div>
        ) : (
          <table className="voice-list">
            <caption className="muted small" style={{ textAlign: 'left' }}>
              Showing {previewRows.length} of {personas.length} agent(s).
            </caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Gender</th>
                <th scope="col">
                  {blendMode === 'blend' ? 'Resolved spec' : 'Resolved voice'}
                </th>
                <th scope="col">Pin</th>
                <th scope="col">Play</th>
              </tr>
            </thead>
            <tbody>
              {previewRows.map((p) => (
                <ZeroVoiceAgentRow
                  key={p.id}
                  agentName={p.name}
                  worldSeedHex={worldSeed}
                  defaultGender={defaultGender}
                  blendMode={blendMode}
                  arcTable={arcTable}
                  xxhashReady={xxhashReady}
                  pin={pins[p.name]}
                  onGenderChange={(g) =>
                    void onAgentGenderChange(p.name, g)
                  }
                  onPinToggleSingle={(vId, g) =>
                    void onPinToggleSingle(p.name, vId, g)
                  }
                  onPinToggleBlend={(spec, g) =>
                    void onPinToggleBlend(p.name, spec, g)
                  }
                  onPlaySingle={(vId) =>
                    void playLocalSingle(vId, `Hello, I am ${p.name}.`)
                  }
                  onPlayBlend={(spec) =>
                    void playLocalBlend(
                      spec,
                      `Hello, I am ${p.name}.`,
                      `row:${p.name}`,
                    )
                  }
                  onCustomize={(initial) => {
                    setCustomizeAgent(p.name);
                    setCustomizeInitial(initial);
                  }}
                  playingKey={playingKey}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {blendMode === 'blend' ? (
        <details
          className="panel"
          open={arcInspectorOpen}
          onToggle={(e) =>
            setArcInspectorOpen((e.target as HTMLDetailsElement).open)
          }
        >
          <summary>
            <strong>Arc-property inspector</strong>
            <span className="muted small">
              {' '}
              — 26×26 heatmaps. Click any cell to audition a blend.
            </span>
          </summary>
          <div style={{ marginTop: '0.5em' }}>
            <ZeroVoiceArcInspector
              arcTable={arcTable}
              onPlayBlend={(spec) =>
                void playLocalBlend(
                  spec,
                  'Hello.',
                  `inspector:${spec.voiceA}:${spec.voiceB}`,
                )
              }
            />
          </div>
        </details>
      ) : null}

      <div className="panel">
        <h3>Voice catalogue</h3>
        <p className="muted small">
          The 26 Kokoro base voices. Stage 7 picks one deterministically;
          Stage 8 blends two of these into billions of unique voices.
        </p>
        <div className="muted small" style={{ fontFamily: 'monospace' }}>
          {KOKORO_VOICES.join(' · ')}
        </div>
        {arcTable && blendMode === 'blend' ? (
          <div className="muted small" style={{ marginTop: '0.5em' }}>
            Arc table:{' '}
            <code>
              example {KOKORO_VOICES[0]} × {KOKORO_VOICES[10]} →{' '}
              {formatSpec({
                voiceA: 0,
                voiceB: 10,
                t: 0.5,
                pitch: 1.0,
                energy: 1.0,
              })}
            </code>
          </div>
        ) : null}
      </div>

      {err ? <div className="status-missing">Error: {err}</div> : null}
      <div className="muted small">
        API base: <code>{API_BASE || '(same origin)'}</code>
      </div>

      <div className="panel">
        <ZeroTTSSettingsPanel
          personaName={activePersonaName}
          worldSeedHex={worldSeed}
          defaultGender={defaultGender}
          enabled={zeroTTSEnabled}
          onToggleEnabled={onZeroTTSEnabledChange}
          fireAck={zeroTTSFireAck}
          fireToolInvoke={zeroTTSFireToolInvoke}
          fireProgress={zeroTTSFireProgress}
          fireError={zeroTTSFireError}
          onToggleFireAck={onZeroTTSFireAckChange}
          onToggleFireToolInvoke={onZeroTTSFireToolInvokeChange}
          onToggleFireProgress={onZeroTTSFireProgressChange}
          onToggleFireError={onZeroTTSFireErrorChange}
          depth={zeroTTSDepth}
          onDepthChange={onZeroTTSDepthChange}
          blendMode={blendMode}
          pinnedSpec={(() => {
            const raw = pins[activePersonaName]?.spec_json;
            if (!raw) return null;
            try {
              const parsed = JSON.parse(raw) as Partial<VoiceSpec>;
              if (
                typeof parsed.voiceA === 'number' &&
                typeof parsed.voiceB === 'number' &&
                typeof parsed.t === 'number' &&
                typeof parsed.pitch === 'number' &&
                typeof parsed.energy === 'number'
              ) {
                return parsed as VoiceSpec;
              }
            } catch {
              /* ignore malformed spec_json */
            }
            return null;
          })()}
        />
      </div>

      {customizeAgent ? (
        <CustomizeSpecDialog
          open={!!customizeAgent}
          agentName={customizeAgent}
          worldSeedHex={worldSeed}
          initialSpec={customizeInitial}
          onClose={() => {
            setCustomizeAgent(null);
            setCustomizeInitial(null);
          }}
          onSave={async (spec) => {
            const name = customizeAgent;
            if (!name) return;
            const existing = pins[name];
            const gender: KokoroGender =
              existing?.gender ?? defaultGender;
            const specJson = JSON.stringify(spec);
            try {
              await upsertPin(name, {
                voice_id: KOKORO_VOICES[spec.voiceA],
                gender,
                world_seed: worldSeed,
                spec_json: specJson,
              });
              setPins((prev) => ({
                ...prev,
                [name]: {
                  voice_id: KOKORO_VOICES[spec.voiceA],
                  gender,
                  world_seed: worldSeed,
                  spec_json: specJson,
                  pinned_at: Math.floor(Date.now() / 1000),
                },
              }));
              onChanged();
            } catch (e) {
              console.warn('[customize] save failed:', e);
              throw e;
            }
          }}
        />
      ) : null}
    </div>
  );
}
